import { clsx } from 'clsx';
import Fuse from 'fuse.js';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    createPalette,
    PaletteButton,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteDef,
    type StyleProp,
    type ViewStyle,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { ImageIcon, LoadingIndicator } from '@gitbook/spine-react';
import { useEventCallback, useLiveRef, useLoadingState } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { fetchCache, getContentKitURLString } from '../cache';
import { resolveDynamicBinding } from './bindings';
import { ContentKitIcon } from './ContentKitIcon';
import type { ContentKitElementProps } from './types';

/**
 * When options is to be lazily loaded, the response must have a shape like this so that
 * the palette can render the options and load more etc.
 *
 * - The `items` property is the list of options to render.
 * - The `nextPage` property is the next page url to load.
 * - The `selected` property  is the list of options that are currently selected options for that palette.
 *   As the list is lazily loaded, we cannot rely on the loaded state to successfully render the label of
 *   the selected options. Thus, the developer can explicitly provide the selected options in the response
 *   by providing the current state of the palette as a `query param` to the API.
 */
type ContentKitSelectLazyOptionsResponse = {
    items: Array<api.ContentKitSelectOption>;
    nextPage?: string;
    selected?: Array<api.ContentKitSelectOption>;
};

/**
 * The response from the API can be either an entire list of options or a lazy response
 * that is paginated.
 */
type ContentKitSelectOptionsResponse =
    | Array<api.ContentKitSelectOption>
    | ContentKitSelectLazyOptionsResponse;

interface ContentKitSelectProps extends ContentKitElementProps<api.ContentKitSelect> {
    style?: StyleProp<ViewStyle>;
}

export type ContentKitSelectGeneric = Omit<api.ContentKitSelect, 'options'> & {
    options:
        | api.ContentKitSelectOption[]
        | {
              /** External source of options. The URL should respond with an array of options. */
              url: string | api.ContentKitURL;
          };
};

export type ContentKitSelectSourceOpenAPISpec = Omit<api.ContentKitSelect, 'options'> & {
    options: { source: 'openapi' };
};

/**
 * Renderer for a select element. It renders static options and loads dynamic ones.
 */
export function ContentKitSelect(props: ContentKitSelectProps): React.ReactElement {
    const { element } = props;

    const { options } = element;

    if ('source' in options) {
        switch (options.source) {
            case 'openapi': {
                const OpenAPISpecSelect = props.remoteComponents['select-source-openapi'];
                return <OpenAPISpecSelect {...props} element={{ ...element, options }} />;
            }
            default:
                assertNever(options.source, 'Unknown source for select options');
        }
    }

    return <ContentKitDefaultSelect {...props} element={{ ...element, options }} />;
}

interface ContentKitDefaultSelectProps extends ContentKitElementProps<ContentKitSelectGeneric> {
    style?: StyleProp<ViewStyle>;
}

function ContentKitDefaultSelect(props: ContentKitDefaultSelectProps): React.ReactElement {
    const { element, localState, dispatchAction, setLocalState } = props;
    const [isDispatching, wrapDispatching] = useLoadingState(false);

    /**
     * Dispatch action (if exists) when local state changes.
     */
    const onValueChange = (updatedState: object) => {
        const onChangeAction = element.onValueChange;
        if (onChangeAction) {
            const action = resolveDynamicBinding(updatedState, onChangeAction);
            wrapDispatching(() => dispatchAction(action));
        }
    };

    const onSelectOption = (option: api.ContentKitSelectOption) => {
        setLocalState((prevState) => {
            const updated = setValueInState(prevState, option.id, element);
            onValueChange(updated);
            return updated;
        });
    };

    const value = getCurrentValuesFromState(localState, element);

    if (Array.isArray(element.options)) {
        return (
            <StaticSelect
                {...props}
                isLoading={isDispatching}
                options={element.options}
                onSelectOption={onSelectOption}
                value={value}
            />
        );
    }

    return (
        <DynamicSelect
            {...props}
            isLoading={isDispatching}
            url={element.options.url}
            onSelectOption={onSelectOption}
            value={value}
        />
    );
}

function StaticSelect(
    props: ContentKitDefaultSelectProps & {
        options: api.ContentKitSelectOption[];
        onSelectOption: (option: api.ContentKitSelectOption) => void;
        value: Set<string>;
        isLoading: boolean;
    }
) {
    const { element, onSelectOption, style, options, isLoading, value } = props;
    const selectedOptions = options.filter((option) => value.has(option.id));

    return (
        <SelectBase
            palette={() =>
                createSelectOptionsPalette({
                    element,
                    options,
                    value,
                    onSelectOption,
                })
            }
            selectedOptions={selectedOptions}
            isLoading={isLoading}
            disabled={element.disabled}
            placeholder={element.placeholder}
            style={style}
        />
    );
}

function DynamicSelect(
    props: ContentKitDefaultSelectProps & {
        url: string | api.ContentKitURL;
        isLoading: boolean;
        onSelectOption: (option: api.ContentKitSelectOption) => void;
        value: Set<string>;
    }
) {
    const { element, localState, style, url, isLoading, onSelectOption, value } = props;

    const [nextPage, setNextPage] = React.useState<
        ContentKitSelectLazyOptionsResponse['nextPage'] | undefined
    >(undefined);
    const [fetchError, setFetchError] = React.useState<string | null>(null);
    const [options, setOptions] = React.useState<api.ContentKitSelectOption[]>([]);
    const [isFetching, wrapFetching] = useLoadingState(true);
    const [selectedOptions, setSelectedOptions] = React.useState<api.ContentKitSelectOption[]>([]);

    const currentOptions: api.ContentKitSelectOption[] = [];
    /**
     * Find the current options from the loaded options list. If the option is not found, the list
     * could be lazily loaded so we fallback to the finding it in selected options.
     */
    value.forEach((v) => {
        const foundInOptions = options.find((option) => option.id === v);
        if (foundInOptions) {
            currentOptions.push(foundInOptions);
        } else {
            const foundInSelected = selectedOptions.find((option) => option.id === v);
            if (foundInSelected) {
                currentOptions.push(foundInSelected);
            }
        }
    });

    /**
     * Handler to fetch options from the provided URL.
     * @returns The complete list of options.
     */
    const onFetchOptions = useEventCallback(async (url: string, merge = false) => {
        setFetchError(null);
        const response = await fetchCache.readAsync(url);

        if (!response.ok) {
            setFetchError(response.error || 'Unexpected error while fetching options');
            return;
        }

        if (response.data) {
            const data = response.data as ContentKitSelectOptionsResponse;
            const fetchedItems = 'items' in data ? data.items : data;

            let nextOptions = fetchedItems;

            if (merge) {
                nextOptions = [...options];
                fetchedItems.forEach((item) => {
                    if (!nextOptions.some((opt) => opt.id === item.id)) {
                        nextOptions.push(item);
                    }
                });
            }

            setOptions(nextOptions);
            setNextPage('nextPage' in data ? data.nextPage : undefined);
            setSelectedOptions(
                'selected' in data && Array.isArray(data.selected) ? data.selected : []
            );

            return nextOptions;
        }
    });

    /**
     * Handler when searching options using a query.
     */
    const onSearchOption = (query: string) => {
        const input = setQueryParamInUrl(url, 'q', query);
        return fetchCache.read(getContentKitURLString(input, localState));
    };

    // This is needed to avoid stale closures for the `onLoadMore` handler
    // that is passed to the palette.
    const nextPageRef = useLiveRef(nextPage);

    /**
     * Handler to fetch more options using pagination.
     * @returns The current list of options after loading more.
     */
    const onLoadMore = async () => {
        if (nextPageRef.current) {
            const nextPageUrl = nextPageRef.current;
            return onFetchOptions(nextPageUrl, true);
        }
    };

    const resolvedURL = React.useMemo(
        () => getContentKitURLString(url, localState),
        [url, localState]
    );

    React.useEffect(() => {
        wrapFetching(async () => {
            await onFetchOptions(resolvedURL);
        });
    }, [resolvedURL, wrapFetching, onFetchOptions]);

    return (
        <SelectBase
            palette={() =>
                createSelectOptionsPalette({
                    element,
                    options,
                    value,
                    onSelectOption,
                    onSearchOption,
                    onLoadMore: nextPage ? onLoadMore : undefined,
                })
            }
            selectedOptions={currentOptions}
            isLoading={isFetching || isLoading}
            error={fetchError}
            disabled={element.disabled}
            placeholder={element.placeholder}
            style={style}
        />
    );
}

function SelectBase(props: {
    palette: () => PaletteDef;
    isLoading: boolean;
    selectedOptions: api.ContentKitSelectOption[];
    error?: string | null;
    disabled?: boolean;
    placeholder?: string;
    style?: StyleProp<ViewStyle>;
}) {
    const { palette, isLoading, error, disabled, placeholder, selectedOptions, style } = props;
    const hasValue = selectedOptions.length > 0;
    return (
        <PaletteButton
            size="medium"
            kind="secondary"
            palette={palette}
            label={
                (hasValue
                    ? selectedOptions.map((option) => option.label).join(', ')
                    : placeholder) || ''
            }
            trailingIcon={isLoading ? LoadingIndicator : error ? Icon.Alert : Icon.ChevronDown}
            title={error || undefined}
            disabled={disabled || isLoading}
            className={clsx('w-full justify-between', !hasValue && 'text-muted', style)}
        />
    );
}

function setValueInState(prevLocalState: object, value: string, element: api.ContentKitSelect) {
    let stateValue: string | string[] = value;

    if (element.multiple) {
        const prevValues = getCurrentValuesFromState(prevLocalState, element);
        if (prevValues.has(value)) {
            prevValues.delete(value);
        } else {
            prevValues.add(value);
        }
        stateValue = Array.from(prevValues);
    }

    return {
        ...prevLocalState,
        [element.state]: stateValue,
    };
}

function getCurrentValuesFromState(localState: object, element: api.ContentKitSelect): Set<string> {
    const fromState = localState[element.state] || element.initialValue || '';
    let asArray = fromState ? (Array.isArray(fromState) ? fromState : [fromState]) : [];

    if (!element.multiple) {
        asArray = asArray.slice(0, 1);
    }

    return new Set(asArray);
}

function setQueryParamInUrl(
    url: string | api.ContentKitURL,
    key: string,
    value: string
): string | api.ContentKitURL {
    if (typeof url === 'string') {
        const u = new URL(url);
        u.searchParams.set(key, value);
        return u.toString();
    }

    return {
        ...url,
        query: {
            ...(url.query || {}),
            [key]: value,
        },
    };
}

/**
 * Filter a set of select options using a query.
 */
function filterSelectOptions(
    query: string,
    commands: api.ContentKitSelectOption[]
): api.ContentKitSelectOption[] {
    if (!query) {
        return commands;
    }

    const fuse = new Fuse(commands, {
        threshold: 0.3,
        location: 0,
        distance: 100,
        includeScore: true,
        minMatchCharLength: 1,
        isCaseSensitive: false,
        keys: ['label'],
    });
    return fuse.search<api.ContentKitSelectOption>(query).map((result) => result.item);
}

function createSelectOptionsPalette(props: {
    element: api.ContentKitSelect;
    options: api.ContentKitSelectOption[];
    value: Set<string>;
    onSelectOption: (option: api.ContentKitSelectOption) => void;
    onSearchOption?: (query: string) => any;
    onLoadMore?: () => Promise<api.ContentKitSelectOption[] | void>;
}): PaletteDef {
    const { element, options, value, onSelectOption, onSearchOption, onLoadMore } = props;
    return createPalette({
        search: Array.isArray(element.options) ? undefined : { placeholder: 'Search…' },
        groups: [
            {
                key: 'query',
                commands: (query) => {
                    // Search options asynchonously if the results have been paginated
                    if (query && !element.acceptInput && onLoadMore && onSearchOption) {
                        const searchRes = onSearchOption(query);
                        if (searchRes.data && Array.isArray(searchRes.data.items)) {
                            return (searchRes.data.items as Array<api.ContentKitSelectOption>)
                                .filter((option) => !options.some((opt) => opt.id === option.id))
                                .map((option) => ({
                                    key: option.id,
                                    title: option.label,
                                    icon: option.icon ? (
                                        typeof option.icon === 'string' ? (
                                            <ContentKitIcon icon={option.icon} />
                                        ) : (
                                            <ImageIcon src={option.icon.source.url} />
                                        )
                                    ) : undefined,
                                    ...(element.multiple
                                        ? {
                                              type: PaletteCommandType.CHECKBOX as const,
                                              selected: value.has(option.id),
                                          }
                                        : {
                                              selected: undefined,
                                          }),
                                    trigger: () => {
                                        onSelectOption(option);
                                    },
                                }));
                        }
                    }

                    return [];
                },
            },
            {
                key: 'options',
                commands: (query) => {
                    const commands: PaletteCommandDef[] = filterSelectOptions(query, options)
                        .concat(element.acceptInput && query ? [{ id: query, label: query }] : [])
                        .map((option) => ({
                            key: option.id,
                            title: option.label,
                            icon: option.icon ? (
                                typeof option.icon === 'string' ? (
                                    <ContentKitIcon icon={option.icon} />
                                ) : (
                                    <ImageIcon src={option.icon.source.url} />
                                )
                            ) : undefined,
                            ...(element.multiple
                                ? {
                                      type: PaletteCommandType.CHECKBOX as const,
                                      selected: value.has(option.id),
                                  }
                                : {
                                      type: PaletteCommandType.RADIO as const,
                                      selected: value.has(option.id),
                                  }),
                            trigger: () => {
                                onSelectOption(option);
                            },
                        }));

                    if (onLoadMore && !query) {
                        commands.push({
                            key: 'load_more',
                            title: 'Load more…',
                            icon: <Icon.RotateLeft />,
                            trigger: async () => {
                                const updatedOptionsList = await onLoadMore();
                                if (updatedOptionsList) {
                                    return createSelectOptionsPalette({
                                        element,
                                        options: updatedOptionsList,
                                        value,
                                        onSelectOption,
                                        onSearchOption,
                                        onLoadMore,
                                    });
                                }
                            },
                        });
                    }
                    return commands;
                },
            },
        ],
    });
}
